// src/AcceptInvitePage.jsx
import { useEffect, useState } from "react";
import { auth, db } from "./firebase.js";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";

export default function AcceptInvitePage() {
  const [status, setStatus] = useState("loading"); // loading | requireLogin | mismatch | processing | done | error
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [hid, setHid] = useState("");
  const [invId, setInvId] = useState("");

  // Force-refresh the ID token so rules see the correct request.auth.token.email
  async function getFreshTokenEmail(u) {
    try {
        await u.getIdToken(true);                 // force refresh
        const res = await u.getIdTokenResult();   // read fresh claims
        const tokenEmail = (res?.claims?.email || u.email || "").trim();
        return tokenEmail;
    } catch {
        return (u.email || "").trim();
    }
  }

  // Send users to the auth screen with the right mode + prefilled email,
  // and remember where to return after they finish auth.
  function goToAuth(mode, invitedEmail) {
    try {
      sessionStorage.setItem("inviteEmail", invitedEmail || "");
      sessionStorage.setItem("inviteReturn", window.location.href);
      const q = new URLSearchParams({ auth: mode, email: invitedEmail || "" });
      window.location.href = `/${q.toString() ? "?" + q.toString() : ""}`;
    } catch { /* no-op */ }
  }

  useEffect(() => {
    // Parse query params
    const params = new URLSearchParams(window.location.search);
    const hidQ = params.get("hid") || "";
    const invQ = params.get("inv") || "";
    const emailQ = params.get("email") || "";
    setHid(hidQ);
    setInvId(invQ);
    setEmail(emailQ);

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setStatus("requireLogin"); return; }

      try {
        setStatus("processing");

        // 1) Fetch invite
        const invRef = doc(db, "households", hidQ, "invites", invQ);
        const invSnap = await getDoc(invRef);
        if (!invSnap.exists()) {
          setStatus("error"); setMsg("Invite not found."); return;
        }
        const inv = invSnap.data();

        // 2) Use a FRESH token email (important right after sign-up)
        const tokenEmail = (await getFreshTokenEmail(u)) || "";
        const userEmail = tokenEmail;                // use token email consistently
        const invEmail = (inv.email || "").trim();

        // 3) Validate invite matches the signed-in user's email and is pending
        if (!userEmail || userEmail.toLowerCase() !== invEmail.toLowerCase()) {
          setStatus("mismatch");
          setMsg(`This invite is for ${invEmail}, but you are signed in as ${userEmail || "(no email)"}.`);
          return;
        }
        if ((inv.status || "pending") !== "pending") {
          setStatus("error"); setMsg(`Invite is ${inv.status || "not pending"}.`); return;
        }

        // 4) Create member doc (guarded by rules; include inviteId!)
        try {
          const memRef = doc(db, "households", hidQ, "members", u.uid);
          await setDoc(memRef, {
            uid: u.uid,
            email: userEmail,
            role: "coHead",
            access: inv.access || "edit",
            createdAt: serverTimestamp(),
            inviteId: invQ, // REQUIRED by rules for self-accept
          }, { merge: false });

          // Ensure the invitee’s user profile exists and points to this household
          await setDoc(doc(db, "users", u.uid), {
                email: userEmail,
                role: "coHead",
                activeHouseholdId: hidQ,
                updatedAt: serverTimestamp(),
                createdAt: serverTimestamp(),
          }, { merge: true });
        } catch (e) {
          console.error("[AcceptInvite] members create failed", e);
          setStatus("error");
          setMsg(`Could not add you as a member. ${e?.message || e}`);
          return;
        }

        // 5) Mark invite accepted (non-fatal if this fails)
        try {
          await updateDoc(invRef, { status: "accepted", updatedAt: serverTimestamp() });
        } catch (e) {
          console.warn("[AcceptInvite] invite status update failed", e);
          setMsg("You're in! (Invite status couldn’t be updated, but your access is active.)");
        }

        setStatus("done");
      } catch (e) {
        setStatus("error"); setMsg(e?.message || String(e));
      }

    });
    return () => unsub();
  }, []);

  const card = { maxWidth: 520, margin: "40px auto", padding: 16, border: "1px solid #eee", borderRadius: 12 };
  const btn = { background: "#00B074", color: "#fff", border: "none", padding: "10px 14px", borderRadius: 10, fontWeight: 700, cursor: "pointer" };
  const link = { color: "#00B074", textDecoration: "underline", cursor: "pointer" };

  if (status === "loading" || status === "processing") return <div style={card}>Processing your invite…</div>;

  if (status === "requireLogin") {
    return (
      <div style={card}>
        <h3>Sign in required</h3>
        <p>Please sign in to accept your invite.</p>
        <button style={btn} onClick={() => (window.location.href = "/")}>Go to Login</button>
      </div>
    );
  }

  if (status === "mismatch") {
    return (
      <div style={card}>
        <h3>Email mismatch</h3>
        <p>{msg}</p>
        <p style={{ marginTop: 8 }}>
          This invite was sent to <strong>{email}</strong>. To continue, either sign in
          or create a new account with that email. We’ll bring you back here to finish.
        </p>

        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button
            style={btn}
            onClick={() => goToAuth("signin", email)}
          >
            Sign in as {email}
          </button>
          <button
            style={{ ...btn, background: "#F4C542", color: "#000" }}
            onClick={() => goToAuth("signup", email)}
          >
            Create account as {email}
          </button>
          <button
            style={{ ...btn, background: "#444" }}
            onClick={async () => { await signOut(auth); window.location.href = "/"; }}
          >
            Sign out
          </button>
        </div>

        <p style={{ marginTop: 10, fontSize: 12, color: "#666" }}>
          Tip: If you already use Trelli with a different email, ask the head of household to re-send the invite to that address.
        </p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={card}>
        <h3>Invite error</h3>
        <p style={{ color: "#b00020" }}>{msg}</p>
        <div style={{ marginTop: 8 }}>
          <span style={link} onClick={() => window.location.reload()}>Try again</span>
        </div>
      </div>
    );
  }

  // done
  return (
    <div style={card}>
      <h3>You're in! 🎉</h3>
      <p>You've joined the household as Co-Head.</p>
      <button style={btn} onClick={() => (window.location.href = "/")}>Open Trelli</button>
    </div>
  );
}
